import React, { ChangeEvent, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  Typography,
  CardMedia,
  Input,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useAddServicesMutation,
  useUpdateServiceMutation,
  useLazyGetServiceByIdQuery,
  useLazyGetSubCategoryQuery,
} from "../../service/services";
import { useLazyMyProfileQuery } from "../../service/auth";
import { useLazyGetCategoryQuery } from "../../service/main";
import { UploadMedia, UploadVideo } from "../../utils/mediaUpload";
import { Category, ServiceDetails } from "../../types/General";
import { useImageVerification } from "../../hooks/imageVerification";
import { showError, showToast } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import CurrencyIcon from "../../components/dirham";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

type Weekday = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";

interface AvailabilityPattern {
  days: Record<Weekday, boolean>;
  startTime: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
}

const AddService = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [images, setImages] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [service, setService] = useState<ServiceDetails | null>(null);

  const [addService, { isLoading: isAdding }] = useAddServicesMutation();
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();
  const [getCategory] = useLazyGetCategoryQuery();
  const [getSubCategory] = useLazyGetSubCategoryQuery();
  const [getServiceById, { isLoading: getServiceByIdLoading }] =
    useLazyGetServiceByIdQuery();
  const [myProfile] = useLazyMyProfileQuery();
  const [userProfile, setUserProfile] = useState<any>(null);
  const { validateImage, loading: loadingImage } = useImageVerification();
  const [isImageProcessing, setIsImageProcessing] = useState<boolean>(false);

  const fetchCategories = async () => {
    try {
      const response = await getCategory({}).unwrap();
      if (response.statusCode === 200) {
        setCategories(
          response?.data?.data?.filter(
            (cat: any) => !cat.isBlocked && !cat.isDeleted
          ) || []
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getSubCategoryApi = async () => {
    try {
      const response = await getSubCategory({
        parentId: formik.values.category,
      }).unwrap();
      if (response.statusCode === 200) {
        setSubCategories(
          response?.data?.data?.filter(
            (sub: any) => !sub.isBlocked && !sub.isDeleted
          ) || []
        );
      } else {
        setSubCategories([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getServiceByIdApi = async () => {
    try {
      const response = await getServiceById({
        id,
      }).unwrap();
      if (response.statusCode === 200) {
        const data: ServiceDetails = response?.data;
        setService(data);

        // Pre-fill subcategories with the current one to ensure it shows while loading list
        if (data.subCategory) {
          setSubCategories([data.subCategory]);
        }

        // Initialize images state
        if (data.media) {
          const serviceImages = data.media
            .filter((m: any) => String(m.type) === "1" || String(m.type) === "2")
            .map((m: any) => m.url);
          setImages(serviceImages);
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const validationSchema = Yup.object({
    category: Yup.string()
      .notOneOf(["default"], "Category is required")
      .required("Category is required"),
    subCategory: Yup.string()
      .notOneOf(["default"], "Sub-category is required")
      .required("Sub-category is required"),
    name: Yup.string()
      .required("Service name is required")
      .matches(/^\S/, "Service name cannot start with a space"),
    description: Yup.string()
      .required("Description is required")
      .matches(/^\S/, "Description cannot start with a space"),
    price: Yup.number()
      .required("Price is required")
      .min(1, "Price must be greater than 0")
      .max(999999999, "Price cannot exceed 9 digits")
      .typeError("Price must be a valid number"),
    priceType: Yup.string().required("Price type is required"),
    isTaxApplicable: Yup.string().required(),
    tax: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .nullable()
      .when("isTaxApplicable", {
        is: "Yes",
        then: (schema) =>
          schema
            .required("Tax is required")
            .min(0, "Tax must be non-negative")
            .max(100, "Tax cannot exceed 100%")
            .typeError("Tax must be a valid number"),
        otherwise: (schema) => schema.optional(),
      }),
    slotTime: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .nullable()
      .required("Duration is required")
      .min(1, "Duration must be greater than 0")
      .max(100000, "Duration is too large")
      .typeError("Duration must be a valid number"),
    maxLimit: Yup.number()
      .required("Max limit is required")
      .min(1, "Max limit must be positive")
      .max(9999999, "Max limit is too large")
      .integer("Max limit must be a whole number")
      .typeError("Max limit must be a valid number"),
    addOns: Yup.string().optional(),
    availabilityPatterns: Yup.array()
      .of(
        Yup.object().shape({
          days: Yup.object().test(
            "at-least-one-day",
            "At least one weekday must be selected",
            (days) => Object.values(days || {}).some((val) => val === true)
          ),
          startTime: Yup.mixed().required("Start time is required"),
          endTime: Yup.mixed().required("End time is required")
            .test(
              "is-greater",
              "End time must be after start time",
              function (value) {
                const { startTime } = this.parent;
                if (!startTime || !value) return true;
                const startMins = dayjs(startTime as any).hour() * 60 + dayjs(startTime as any).minute();
                const endMins = dayjs(value as any).hour() * 60 + dayjs(value as any).minute();
                return endMins > startMins;
              }
            ),
        })
      )
      .min(1, "At least one availability pattern is required")
      .test(
        "no-overlap",
        "Overlapping time slots for the same day across patterns are not allowed",
        (patterns) => {
          if (!patterns) return true;
          for (let i = 0; i < patterns.length; i++) {
            for (let j = i + 1; j < patterns.length; j++) {
              const p1 = patterns[i] as any;
              const p2 = patterns[j] as any;
              
              const commonDays = Object.keys(p1.days || {}).filter(
                (day) => p1.days[day] && p2.days[day]
              );
              
              if (commonDays.length > 0) {
                const start1Mins = dayjs(p1.startTime).hour() * 60 + dayjs(p1.startTime).minute();
                const end1Mins = dayjs(p1.endTime).hour() * 60 + dayjs(p1.endTime).minute();
                const start2Mins = dayjs(p2.startTime).hour() * 60 + dayjs(p2.startTime).minute();
                const end2Mins = dayjs(p2.endTime).hour() * 60 + dayjs(p2.endTime).minute();
                
                if (start1Mins < end2Mins && start2Mins < end1Mins) {
                  return false;
                }
              }
            }
          }
          return true;
        }
      ),
    files: Yup.array().min(1, "At least one image is required"),
  });

  const formik = useFormik({
    initialValues: {
      category: service?.categoryId ? String(service.categoryId) : "default",
      subCategory: service?.subCategoryId
        ? String(service.subCategoryId)
        : "default",
      name: service?.name || "",
      description: service?.description || "",
      price: service?.price || "",
      priceType: service?.priceType || "1",
      isTaxApplicable: (service?.tax && Number(service?.tax) > 0) ? "Yes" : "No",
      tax: (service?.tax && Number(service?.tax) > 0) ? service.tax : "",
      slotTime: service?.slotTime || "",
      maxLimit: service?.maxLimit ?? (service as any)?.maxlimit ?? "",
      addOns: service?.addOns || "",
      availabilityPatterns: service?.availability?.length
        ? service.availability.map((p: any) => ({
          days: {
            Mon: !!(p.mon ?? p.days?.mon),
            Tue: !!(p.tue ?? p.days?.tue),
            Wed: !!(p.wed ?? p.days?.wed),
            Thu: !!(p.thu ?? p.days?.thu),
            Fri: !!(p.fri ?? p.days?.fri),
            Sat: !!(p.sat ?? p.days?.sat),
            Sun: !!(p.sun ?? p.days?.sun),
          },
          startTime: p.startTime ? dayjs(p.startTime).local() : dayjs(),
          endTime: p.endTime ? dayjs(p.endTime).local() : dayjs(),
        }))
        : [
          {
            days: {
              Mon: false,
              Tue: false,
              Wed: false,
              Thu: false,
              Fri: false,
              Sat: false,
              Sun: false,
            },
            startTime: dayjs("09:00", "HH:mm"),
            endTime: dayjs("17:00", "HH:mm"),
          },
        ],
      files: (service?.media
        ? new Array(
          service.media.filter((m: any) => String(m.type) === "1" || String(m.type) === "2").length
        ).fill(null)
        : []) as (File | null)[],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Upload Images/Videos and merge with existing ones
        const media = await Promise.all(
          images?.map(async (image, i) => {
            if (values.files[i]) {
              const res = await UploadMedia(values.files[i]);
              const fileType = values.files[i]?.type.startsWith("video/") ? 2 : 1;
              return {
                type: fileType, // image or video
                url:
                  res.data?.url ||
                  res.data?.data?.url ||
                  res.data?.imageUrl ||
                  res.data?.image ||
                  (typeof res.data === "string" ? res.data : ""),
              };
            } else if (image && !image.startsWith("blob:")) {
              const isVideo =
                service?.media?.find(
                  (m: any) => m.url === image && String(m.type) === "2"
                ) || image.match(/\.(mp4|webm|ogg|mov|mkv)$/i);
              return {
                type: isVideo ? 2 : 1, // image or video
                url: image, // existing URL
              };
            }
            return null;
          })
        );

        const filteredMedia = media.filter((m) => m !== null);

        // Format availability
        const availability = values.availabilityPatterns.map(
          (pattern: any) => ({
            days: {
              mon: pattern.days.Mon,
              tue: pattern.days.Tue,
              wed: pattern.days.Wed,
              thu: pattern.days.Thu,
              fri: pattern.days.Fri,
              sat: pattern.days.Sat,
              sun: pattern.days.Sun,
            },
            startTime: pattern.startTime.toISOString(),
            endTime: pattern.endTime.toISOString(),
          })
        );

        const body = {
          categoryId: values.category,
          subCategoryId: values.subCategory,
          name: values.name,
          description: values.description,
          price: Number(values.price),
          priceType: Number(values.priceType),
          tax: values.isTaxApplicable === "Yes" ? Number(values.tax) : 0,
          slotTime: values.slotTime ? Number(values.slotTime) : 0,
          maxLimit: Number(values.maxLimit),
          addOns: values.addOns,
          availability,
          media: filteredMedia,
          isActive: true,
        };

        let response;
        if (id) {
          response = await updateService({ id, body }).unwrap();
        } else {
          response = await addService(body).unwrap();
        }

        if (response.statusCode === 200 || response.statusCode === 201) {
          showToast(
            id ? "Service updated successfully" : "Service added successfully"
          );
          navigate("/service-listing");
        } else {
          showError(response.message || "Something went wrong");
        }
      } catch (error: any) {
        showError(error?.data?.message || "An error occurred");
        console.error(error);
      }
    },
  });

  const handleDayToggle = (index: number, day: Weekday) => {
    const updated = [...formik.values.availabilityPatterns];
    const targetPattern = updated[index];
    if (targetPattern) {
      updated[index] = {
        ...targetPattern,
        days: {
          ...targetPattern.days,
          [day]: !targetPattern.days[day],
        },
      };
      formik.setFieldValue("availabilityPatterns", updated);
    }
  };

  const handleTimeChange = (
    index: number,
    field: "startTime" | "endTime",
    newValue: dayjs.Dayjs | null
  ) => {
    const updated = [...formik.values.availabilityPatterns];
    const targetPattern = updated[index];
    if (targetPattern) {
      updated[index] = {
        ...targetPattern,
        [field]: newValue || dayjs(),
      };
      formik.setFieldValue("availabilityPatterns", updated);
    }
  };

  const addAvailabilityPattern = () => {
    formik.setFieldValue("availabilityPatterns", [
      ...formik.values.availabilityPatterns,
      {
        days: {
          Mon: false,
          Tue: false,
          Wed: false,
          Thu: false,
          Fri: false,
          Sat: false,
          Sun: false,
        },
        startTime: dayjs("09:00", "HH:mm"),
        endTime: dayjs("17:00", "HH:mm"),
      },
    ]);
  };

  const removeAvailabilityPattern = (index: number) => {
    const updated = formik.values.availabilityPatterns.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("availabilityPatterns", updated);
  };

  // Handle Image Upload
  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      if (images.length + selectedFiles.length > 7) {
        showError("You can only upload up to 7 images.");
        return;
      }

      setIsImageProcessing(true);
      const newImages = [...images];
      const newFiles = [...formik.values.files];

      try {
        for (const file of Array.from(selectedFiles)) {
          const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
          if (file.size > MAX_FILE_SIZE) {
            showError("File size exceeds the allowed limit");
            continue;
          }

          const isVideo = file.type.startsWith("video/");
          if (!file.type.startsWith("image/") && !isVideo) {
            showError("Unsupported file format");
            continue;
          }

          let uploadRes;
          if (isVideo) {
            uploadRes = await UploadVideo(file, true);
          } else {
            const result = await validateImage(file);
            if (!result.valid) {
              showError(result.error);
              continue;
            }
            uploadRes = await UploadMedia(file, false, true);
          }

          if (uploadRes.statusCode === 200 || uploadRes.statusCode === 201) {
            const url =
              uploadRes.data?.url ||
              uploadRes.data?.data?.url ||
              uploadRes.data?.imageUrl ||
              uploadRes.data?.image ||
              (typeof uploadRes.data === "string" ? uploadRes.data : "");
            if (url) {
              newImages.push(url);
              newFiles.push(null); // Mark as uploaded
              if (isVideo) {
                showToast("Video uploaded successfully.");
              } else {
                showToast("Image uploaded successfully.");
              }
            }
          } else {
            showError("Upload failed due to a network/server error");
          }
        }
        setImages(newImages);
        formik.setFieldValue("files", newFiles);
      } catch (err) {
        showError("Upload failed due to a network/server error");
      } finally {
        setIsImageProcessing(false);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newFiles = formik.values.files.filter((_, i) => i !== index);
    setImages(newImages);
    formik.setFieldValue("files", newFiles);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formik.values.category !== "default") {
      getSubCategoryApi();
    }
  }, [formik.values.category]);

  useEffect(() => {
    if (id) {
      getServiceByIdApi();
    } else {
      myProfile({}).unwrap().then((res) => {
        if (res.statusCode === 200) {
          setUserProfile(res.data);
          const sp = res.data?.sp;
          if (sp) {
            let catId = sp.categoryId || sp.category;
            if (!catId && sp.categories && sp.categories.length > 0) {
              const cat = sp.categories[0];
              catId = cat.categoryId || cat._id || cat;
            }
            if (catId) {
              formik.setFieldValue("category", String(catId));
            }
          }
        }
      }).catch(console.error);
    }
  }, [id, userProfile]);

  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      if (firstErrorKey) {
        const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(`svc-${firstErrorKey}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  }, [formik.submitCount, formik.isValid]);

  return (
    <>
      <Loader isLoad={isAdding || isUpdating || getServiceByIdLoading} />
      <div className="main_title">
        <h1>{id ? "Edit Service" : "Add Service"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/service-listing")}>
            Manage Services
          </button>{" "}
          - {id ? "Edit Service" : "Add Service"}
        </p>
      </div>

      <div className="cards custom_form_details">
        <div className="form">
          <div className="gap_m w_100">
            {/* Category */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-category">Select Category</label>
              <Select
                fullWidth
                name="category"
                value={formik.values.category}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldValue("subCategory", "default");
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.category && Boolean(formik.errors.category)
                }
                disabled
                inputProps={{ id: "svc-category" }}
              >
                <MenuItem disabled value="default">
                  Select Category
                </MenuItem>
                {categories?.map((cat) => (
                  <MenuItem key={cat?.id} value={cat?.id}>
                    {cat?.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <p className="err_txt">
                  {formik.errors.category}
                </p>
              )}
            </div>

            {/* Sub-category */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-subCategory">Select Sub-category</label>
              <Select
                fullWidth
                name="subCategory"
                value={formik.values.subCategory}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.subCategory &&
                  Boolean(formik.errors.subCategory)
                }
                inputProps={{ id: "svc-subCategory" }}
              >
                <MenuItem disabled value="default">
                  Select Sub-category
                </MenuItem>
                {subCategories?.map((sub) => (
                  <MenuItem key={sub?.id} value={sub?.id}>
                    {sub?.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subCategory && formik.errors.subCategory && (
                <p className="err_txt">
                  {formik.errors.subCategory}
                </p>
              )}
            </div>

            {/* Service Name */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-name">Enter Service Name</label>
              <TextField
                id="svc-name"
                name="name"
                placeholder="Enter service name"
                fullWidth
                hiddenLabel
                value={formik.values.name}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label className="required" htmlFor="svc-description">Enter Description of Service</label>
              <TextField
                id="svc-description"
                name="description"
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe the service, inclusions, etc."
                value={formik.values.description}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Price */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-price">Service Price</label>
              <TextField
                id="svc-price"
                name="price"
                placeholder="Enter price"
                fullWidth
                hiddenLabel
                type="number"
                value={formik.values.price}
                inputProps={{ maxLength: 9 }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "0") return;
                  if (val === "" || (Number(val) <= 999999999 && !Number.isNaN(Number(val)))) {
                    formik.handleChange(e);
                  }
                }}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /></InputAdornment>
                }}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Price Type */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-priceType">Price Type</label>
              <Select
                fullWidth
                name="priceType"
                value={formik.values.priceType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.priceType && Boolean(formik.errors.priceType)
                }
                inputProps={{ id: "svc-priceType" }}
              >
                <MenuItem value="1">Hourly</MenuItem>
                <MenuItem value="2">Fixed</MenuItem>
              </Select>
              {formik.touched.priceType && formik.errors.priceType && (
                <p className="err_txt">
                  {formik.errors.priceType}
                </p>
              )}
            </div>

            {/* Tax Applicable */}
            <div className="control_group w_33">
              <label className="required" htmlFor="svc-isTaxApplicable">Tax Applicable</label>
              <Select
                fullWidth
                name="isTaxApplicable"
                value={formik.values.isTaxApplicable}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (e.target.value === "No") {
                    formik.setFieldValue("tax", "");
                  }
                }}
                onBlur={formik.handleBlur}
                inputProps={{ id: "svc-isTaxApplicable" }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </div>

            {/* Tax */}
            {formik.values.isTaxApplicable === "Yes" && (
              <div className="control_group w_33">
                <label className="required" htmlFor="svc-tax">Tax (Percentage)</label>
                <TextField
                  id="svc-tax"
                  name="tax"
                  placeholder="Enter tax percentage"
                  fullWidth
                  hiddenLabel
                  type="number"
                  value={formik.values.tax}
                  inputProps={{ maxLength: 3 }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || (Number(val) <= 100 && !Number.isNaN(Number(val)))) {
                      formik.handleChange(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.tax && Boolean(formik.errors.tax)}
                  helperText={formik.touched.tax && formik.errors.tax as string}
                  FormHelperTextProps={{
                    className: "err_txt",
                  }}
                />
              </div>
            )}

            {/* Duration / Time Slots */}
            <div className="control_group w_50">
              <label className="required" htmlFor="svc-slotTime">Duration (in hours) </label>
              <TextField
                id="svc-slotTime"
                name="slotTime"
                placeholder="E.g., 1hour"
                fullWidth
                hiddenLabel
                type="number"
                value={formik.values.slotTime}
                inputProps={{ maxLength: 6 }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "0") return;
                  if (val === "" || (Number(val) <= 100000 && /^\d*$/.test(val))) {
                    formik.handleChange(e);
                  }
                }}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.slotTime && Boolean(formik.errors.slotTime)
                }
                helperText={formik.touched.slotTime && formik.errors.slotTime}
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Max Bookings */}
            <div className="control_group w_50">
              <label className="required" htmlFor="svc-maxLimit">Set Maximum Bookings Per Day</label>
              <TextField
                id="svc-maxLimit"
                name="maxLimit"
                placeholder="Enter max bookings per day"
                fullWidth
                hiddenLabel
                type="number"
                value={formik.values.maxLimit}
                inputProps={{ maxLength: 7 }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "0") return;
                  if (val === "" || (Number(val) <= 9999999 && /^\d*$/.test(val))) {
                    formik.handleChange(e);
                  }
                }}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.maxLimit && Boolean(formik.errors.maxLimit)
                }
                helperText={
                  formik.touched.maxLimit && (formik.errors.maxLimit as string)
                }
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>

            {/* Add-ons */}
            <div className="control_group w_100">
              <label htmlFor="svc-addOns">Add Optional Extras or Add-ons (Optional)</label>
              <TextField
                id="svc-addOns"
                name="addOns"
                fullWidth
                multiline
                minRows={2}
                placeholder="List optional extras (e.g., deep cleaning, express service)..."
                value={formik.values.addOns}
                onChange={(e) => {
                  if (e.target.value.startsWith(" ")) return;
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={formik.touched.addOns && Boolean(formik.errors.addOns)}
                helperText={formik.touched.addOns && formik.errors.addOns}
                FormHelperTextProps={{
                  className: "err_txt",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Availability Section */}
      <div className="cards availability_section" id="svc-availabilityPatterns">
        <Typography variant="h6" fontWeight={600} mb={2}>
          Set Service Availability
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {formik.values.availabilityPatterns?.map(
            (pattern: any, index: number) => (
              <Box
                key={index}
                border="1px solid #ddd"
                borderRadius="8px"
                padding="16px"
                marginBottom="16px"
              >
                <Typography fontWeight={500} mb={1}>
                  Availability Pattern {index + 1}
                </Typography>

                <Grid container spacing={2}>
                  {/* Days of the week */}
                  <Grid item xs={12} sm={8}>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      {(Object.keys(pattern?.days) as Weekday[]).map((day) => (
                        <FormControlLabel
                          key={day}
                          control={
                            <Checkbox
                              checked={pattern?.days[day]}
                              onChange={() => handleDayToggle(index, day)}
                            />
                          }
                          label={day}
                        />
                      ))}
                    </Box>
                    {formik.touched.availabilityPatterns?.[index] &&
                      (formik.errors.availabilityPatterns?.[index] as any)
                        ?.days && (
                        <p className="err_txt">
                          {
                            (formik.errors.availabilityPatterns?.[index] as any)
                              ?.days
                          }
                        </p>
                      )}
                  </Grid>

                  {/* Time Pickers */}
                  <Grid item xs={12} sm={4}>
                    <div className="form">
                      <Box display="flex" gap={2}>
                        <TimePicker
                          label="Start Time"
                          value={pattern?.startTime}
                          onChange={(val) =>
                            handleTimeChange(index, "startTime", val)
                          }
                        />
                        <TimePicker
                          label="End Time"
                          value={pattern?.endTime}
                          onChange={(val) =>
                            handleTimeChange(index, "endTime", val)
                          }
                        />
                      </Box>
                      {formik.touched.availabilityPatterns?.[index] &&
                        (formik.errors.availabilityPatterns?.[index] as any)
                          ?.endTime && (
                          <p className="err_txt" style={{ marginTop: '4px' }}>
                            {
                              (formik.errors.availabilityPatterns?.[index] as any)
                                ?.endTime
                            }
                          </p>
                        )}
                    </div>
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <Button
                    color="error"
                    onClick={() => removeAvailabilityPattern(index)}
                  >
                    Remove Pattern
                  </Button>
                </Box>
              </Box>
            )
          )}

          <Button variant="outlined" onClick={addAvailabilityPattern}>
            + Add Another Weekly Pattern
          </Button>
          {typeof formik.errors.availabilityPatterns === "string" && (
            <Box mt={1}>
              <p className="err_txt">
                {formik.errors.availabilityPatterns}
              </p>
            </Box>
          )}
        </LocalizationProvider>
      </div>

      {/* Upload Section */}
      <div className="cards upload_section" id="svc-files">
        <Typography style={{ fontSize: '20px' }} variant="h6" fontWeight={600} mb={2}>
          Upload Images <span style={{ fontSize: '16px' }}>
            (Recommended: 375*200 px)
          </span>
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
          {/* Uploader Input */}
          <label className="upload_image" htmlFor="icon-button-file-multi">
            <Input
              sx={{ display: "none" }}
              id="icon-button-file-multi"
              type="file"
              inputProps={{
                accept: "image/*,video/*",
                multiple: true,
              }}
              onChange={handleImageChange}
            />
            <span className="upload_image_holder square">
              <figure className="add_img">
                <img src="/static/images/placeholder.png" alt="icon" />
                <span className="upload_img_icon">
                  <img src="/static/images/edit2_icon.svg" alt="edit" />
                </span>
                {(isImageProcessing || loadingImage) && (
                  <div className="image_loader_overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.7)',
                    borderRadius: '8px'
                  }}>
                    <CircularProgress size={24} />
                  </div>
                )}
              </figure>
            </span>
          </label>

          {/* Previews */}
          {images.map((image, i) => {
            const isVideo = formik.values.files[i]
              ? formik.values.files[i]?.type.startsWith("video/")
              : service?.media?.find((m: any) => m.url === image && String(m.type) === "2") || image?.match(/\.(mp4|webm|ogg|mov|mkv)$/i);

            return (
              <div key={i} className="upload_image">
                <div className="upload_image_holder square">
                  <figure>
                    {isVideo ? (
                      <CardMedia
                        component="video"
                        src={image || ""}
                        controls
                        style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        image={image || ""}
                        alt={`upload-${i}`}
                      />
                    )}
                  </figure>
                  <CloseIcon onClick={() => handleRemoveImage(i)} />
                </div>
              </div>
            );
          })}
        </Box>
        {formik.touched.files && formik.errors.files && (
          <Box mt={1}>
            <p className="err_txt">
              {formik.errors.files as string}
            </p>
          </Box>
        )}

        <div className="w_100 mt_20">
          <Button
            className="btn btn_primary w_100"
            onClick={() => {
              formik.validateForm().then((errors) => {
                if (errors.slotTime) {
                  showError(errors.slotTime as string);
                }
              });
              formik.handleSubmit();
            }}
            disabled={isAdding || isUpdating}
          >
            {id ? "Update Service" : "Add Service"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddService;
